import { getAhaMomentImageSource } from '@fruitful/conversational-mm';
import { ConversationalMMFullscreenResults } from '@fruitful/conversational-mm/src/fullscreen/ConversationalMMFullscreenResults';
import { formatDollars, lowercaseFirst } from '@fruitful/fruitils';
import {
    OnboardingMoneyMapPreview,
    type OnboardingMoneyMapPreviewMode,
} from '@fruitful/money-map';
import {
    type AhaResult,
    type OnboardingChatResults,
    type RuleSetDisplay,
} from '@fruitful/types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Modal, StyleSheet } from 'react-native';
import Animated, {
    Easing,
    interpolate,
    runOnJS,
    useAnimatedStyle,
    useSharedValue,
    withTiming,
} from 'react-native-reanimated';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import {
    ArrowDown,
    BodyMediumRegular,
    FontFamilyName,
    GRADIENT_COLOR_BOTTOM,
    GRADIENT_COLOR_TOP,
    H5Emphasized,
    H5Regular,
    Icon,
    Image,
    isNonMobileWeb,
    LabelRegular,
    Text,
    useWindowDimensions,
    View,
    XStack,
    YStack,
} from 'smoothie';
import { LinearGradient } from 'tamagui/linear-gradient';

import {
    trackCMMEvent,
    useConversationalMMGuidedTooltips,
} from '../../../llm-service/hooks/useConversationalMMAdapters';
import { useGetActiveGuides } from '../../../onboarding/hooks/useGetActiveGuides';

const EXPAND_DURATION = 450;
const EXPAND_EASING = Easing.out(Easing.cubic);
const COLLAPSE_DURATION = 350;
const COLLAPSE_EASING = Easing.in(Easing.cubic);

const INVEST_READY_COPY =
    "Your optimized investment system is ready! Based on everything you've told us, we estimate you could have...";

interface TileLayout {
    x: number;
    y: number;
    width: number;
    height: number;
}

interface V2DisplayData {
    headline: string;
    dateSuffix: string;
    mode: OnboardingMoneyMapPreviewMode;
    showSystemPreview: boolean;
    adjusted: {
        incomeTotalCents: number;
        billsTotalCents: number;
        spendTotalCents: number;
        goalsTotalCents: number;
    };
    ruleSetDisplays: RuleSetDisplay[] | null;
}

interface ExpandingGoalTileOverlayProps {
    tileLayout: TileLayout;
    ahaResult: AhaResult;
    results: OnboardingChatResults;
    onClose: () => void;
    isVersion2?: boolean;
    v2DisplayData?: V2DisplayData;
}

export const ExpandingGoalTileOverlay = ({
    tileLayout,
    ahaResult,
    results,
    onClose,
    isVersion2 = false,
    v2DisplayData,
}: ExpandingGoalTileOverlayProps) => {
    const { top } = useSafeAreaInsets();
    const { width: screenWidth, height: screenHeight } = useWindowDimensions();
    const topPadding = top > 0 ? top : 16;
    const { hasSeenGuidedTooltips, onGuidedTooltipsSeen } =
        useConversationalMMGuidedTooltips();
    const { data: guides } = useGetActiveGuides({});

    const progress = useSharedValue(0);
    const resultsOpacity = useSharedValue(0);
    const [expanded, setExpanded] = useState(false);

    const { displayHeadline, image } = ahaResult;
    const isInvesting = ahaResult.type === 'investing';
    const debtFreeDate = !isInvesting ? ahaResult.debtFreeDate : '';
    const imageSource = getAhaMomentImageSource(image);

    const onExpandComplete = useCallback(() => {
        setExpanded(true);
        resultsOpacity.value = withTiming(1, { duration: 300 });
    }, []);

    useEffect(() => {
        progress.value = withTiming(
            1,
            { duration: EXPAND_DURATION, easing: EXPAND_EASING },
            (finished) => {
                if (finished === true) {
                    runOnJS(onExpandComplete)();
                }
            },
        );
    }, []);

    const handleClose = useCallback(() => {
        resultsOpacity.value = 0;
        setExpanded(false);
        progress.value = withTiming(
            0,
            { duration: COLLAPSE_DURATION, easing: COLLAPSE_EASING },
            (finished) => {
                if (finished === true) {
                    runOnJS(onClose)();
                }
            },
        );
    }, [onClose, progress]);

    // Stable reference — this flows into ConversationalMMFullscreenResults'
    // context value, and a fresh object literal here would force the whole
    // slide tree to re-render on every one of this component's own
    // animation-driven re-renders (see handleClose above).
    const terminalAction = useMemo(
        () => ({
            mode: 'exit' as const,
            label: 'Back to homepage',
            onPress: handleClose,
        }),
        [handleClose],
    );

    const backgroundStyle = useAnimatedStyle(() => ({
        position: 'absolute',
        left: interpolate(progress.value, [0, 1], [tileLayout.x, 0]),
        top: interpolate(progress.value, [0, 1], [tileLayout.y, 0]),
        width: interpolate(
            progress.value,
            [0, 1],
            [tileLayout.width, screenWidth],
        ),
        height: interpolate(
            progress.value,
            [0, 1],
            [tileLayout.height, screenHeight],
        ),
        borderRadius: interpolate(progress.value, [0, 1], [24, 0]),
        overflow: 'hidden' as const,
    }));

    const imageEndSize = isInvesting ? 280 : 240;
    const imageStyle = useAnimatedStyle(() => ({
        width: interpolate(
            progress.value,
            [0, 1],
            [tileLayout.width - 48, imageEndSize],
        ),
        height: interpolate(progress.value, [0, 1], [180, imageEndSize]),
    }));

    const resultsStyle = useAnimatedStyle(() => ({
        ...StyleSheet.absoluteFillObject,
        opacity: resultsOpacity.value,
    }));

    return (
        <Modal visible transparent animationType="none" statusBarTranslucent>
            <Animated.View style={styles.container}>
                <Animated.View style={backgroundStyle}>
                    <LinearGradient
                        colors={[GRADIENT_COLOR_TOP, GRADIENT_COLOR_BOTTOM]}
                        start={[0, 0]}
                        end={[0, 1]}
                        style={StyleSheet.absoluteFill}
                    />
                    {!expanded && (
                        <YStack
                            flex={1}
                            width="100%"
                            alignItems="center"
                            justifyContent="center"
                            paddingTop={topPadding}
                        >
                            <YStack
                                alignItems="center"
                                justifyContent="center"
                                alignSelf="center"
                                gap={24}
                                marginTop="auto"
                                maxWidth={
                                    isNonMobileWeb ? 560 : screenWidth - 16
                                }
                            >
                                {isVersion2 && v2DisplayData != null ? (
                                    <>
                                        <XStack
                                            flexWrap="wrap"
                                            justifyContent="center"
                                            gap={4}
                                            paddingHorizontal={24}
                                            maxWidth={354}
                                            alignSelf="center"
                                        >
                                            <Text
                                                style={{
                                                    fontFamily:
                                                        FontFamilyName.SourceSerifRegular,
                                                }}
                                                fontSize={40}
                                                lineHeight={40}
                                                letterSpacing={-2}
                                                textAlign="center"
                                            >
                                                {v2DisplayData.headline}
                                                {v2DisplayData.dateSuffix !==
                                                    '' &&
                                                    ` ${v2DisplayData.dateSuffix}`}
                                            </Text>
                                        </XStack>
                                        <View
                                            style={{
                                                width: '100%',
                                                maxWidth: 354,
                                                height: 374,
                                            }}
                                            overflow="hidden"
                                            alignSelf="center"
                                        >
                                            <OnboardingMoneyMapPreview
                                                mode={v2DisplayData.mode}
                                                disableAnimations
                                                incomeTotalCents={
                                                    v2DisplayData.adjusted
                                                        .incomeTotalCents
                                                }
                                                billsTotalCents={
                                                    v2DisplayData.adjusted
                                                        .billsTotalCents
                                                }
                                                spendTotalCents={
                                                    v2DisplayData.adjusted
                                                        .spendTotalCents
                                                }
                                                goalsTotalCents={
                                                    v2DisplayData.adjusted
                                                        .goalsTotalCents
                                                }
                                                ruleSetDisplays={
                                                    v2DisplayData.ruleSetDisplays
                                                }
                                            />
                                        </View>
                                    </>
                                ) : isInvesting ? (
                                    <>
                                        <BodyMediumRegular
                                            textAlign="center"
                                            paddingHorizontal={24}
                                        >
                                            {INVEST_READY_COPY}
                                        </BodyMediumRegular>
                                        <Text
                                            fontSize={70}
                                            lineHeight={62}
                                            letterSpacing={-3.5}
                                            textAlign="center"
                                            style={{
                                                fontFamily:
                                                    FontFamilyName.SourceSerifRegular,
                                            }}
                                        >
                                            {formatDollars(
                                                ahaResult.projectedTotalCents /
                                                    100,
                                                false,
                                            )}
                                            {'\n'}invested by{'\n'}
                                            {ahaResult.projectedDate}
                                        </Text>
                                        <Animated.View style={imageStyle}>
                                            <Image
                                                source={imageSource}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                }}
                                                resizeMode="contain"
                                            />
                                        </Animated.View>
                                    </>
                                ) : (
                                    <>
                                        <Animated.View style={imageStyle}>
                                            <Image
                                                source={imageSource}
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                }}
                                                resizeMode="contain"
                                            />
                                        </Animated.View>
                                        <YStack
                                            gap={8}
                                            alignItems="center"
                                            justifyContent="center"
                                        >
                                            <XStack
                                                flexWrap="wrap"
                                                justifyContent="center"
                                                gap={4}
                                            >
                                                <H5Regular>
                                                    {displayHeadline}
                                                </H5Regular>
                                                {debtFreeDate !== '' && (
                                                    <H5Emphasized>
                                                        {debtFreeDate}
                                                    </H5Emphasized>
                                                )}
                                            </XStack>
                                            <BodyMediumRegular
                                                textAlign="center"
                                                paddingHorizontal={24}
                                            >
                                                Your Money Map is ready!
                                                {debtFreeDate !== '' &&
                                                    ` Based on everything you've told us, we estimate that you could ${displayHeadline.toLowerCase().startsWith('debt') ? 'be ' : ''}${lowercaseFirst(displayHeadline)} ${debtFreeDate}.`}
                                            </BodyMediumRegular>
                                        </YStack>
                                    </>
                                )}
                            </YStack>
                            <YStack
                                marginTop="auto"
                                gap={14}
                                paddingTop={114}
                                paddingBottom={120}
                                justifyContent="center"
                                alignItems="center"
                                opacity={0}
                                pointerEvents="none"
                            >
                                <LabelRegular>SCROLL</LabelRegular>
                                <Icon icon={ArrowDown} />
                            </YStack>
                        </YStack>
                    )}
                </Animated.View>

                {expanded && (
                    <Animated.View style={resultsStyle}>
                        <ConversationalMMFullscreenResults
                            results={results}
                            terminalAction={terminalAction}
                            onClose={handleClose}
                            showCloseButton
                            skipCultivating
                            skipEnteringAnimation
                            hasSeenGuidedTooltips={hasSeenGuidedTooltips}
                            onGuidedTooltipsSeen={onGuidedTooltipsSeen}
                            onTrackEvent={trackCMMEvent}
                            useNewArtifact={isVersion2}
                            useGradientBackground={isVersion2}
                            guides={guides}
                            skipSummary={isVersion2}
                        />
                    </Animated.View>
                )}
            </Animated.View>
        </Modal>
    );
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
    },
});
